/**
 * v130 — ShotPlot.
 *
 * Tap-to-place SVG target. Coordinate system: 1 inch = 10 SVG units.
 * Bullseye is at (0,0). Right of POA = positive X, below POA = positive Y.
 *
 * Mobile-first. Touch target = the entire SVG. Touch + click handled
 * uniformly via pointerdown.
 *
 * Visual:
 *   - Black-on-white 8-ring target (12-in maximum diameter)
 *   - Dashed crosshairs through POA
 *   - Past shots: small ember dots, opacity decays with age within the session
 *   - Pending placement: cyan ring at tap location with confirm "Log shot"
 *
 * Props:
 *   shots        — already-placed RichShots to display as faded dots
 *   onPlace      — fired with (hitXIn, hitYIn) when the user taps
 *   pending      — optional preview dot at this offset before commit
 *   targetType   — '3d' | 'paper' | '3-spot' | 'block' | 'custom' — drives ring count
 */

import { useRef, type PointerEvent } from 'react';
import type { RichShot } from '../v2-schema';

interface ShotPlotProps {
  shots: readonly RichShot[];
  pending?: { x: number; y: number } | null;
  onPlace: (xIn: number, yIn: number) => void;
  targetType?: '3d' | 'paper' | '3-spot' | 'block' | 'custom';
  /** Half-extent in inches the target view covers. Default 6 (12-in target). */
  halfExtentIn?: number;
}

// Default ring layout — radii in inches from center, outer to inner.
// 3-spot uses tighter rings (NFAA blue 5-spot semantics).
function getRings(targetType: ShotPlotProps['targetType']): number[] {
  switch (targetType) {
    case '3-spot':
      return [3.5, 2.5, 1.75, 1.0, 0.5];
    case 'paper':
      return [5, 3.5, 2, 1, 0.4];
    case 'block':
      return [4, 3, 2, 1];
    case '3d':
    default:
      return [5.5, 4, 2.5, 1.25, 0.5];
  }
}

export function ShotPlot({
  shots,
  pending,
  onPlace,
  targetType = '3d',
  halfExtentIn = 6,
}: ShotPlotProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const VIEW = halfExtentIn * 2 * 10; // svg units: 1 in = 10
  const HALF = VIEW / 2;
  const rings = getRings(targetType);

  const handlePointer = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // Map screen px → svg viewBox coords → inches relative to POA
    const xView = ((e.clientX - rect.left) / rect.width) * VIEW;
    const yView = ((e.clientY - rect.top) / rect.height) * VIEW;
    const xIn = (xView - HALF) / 10;
    const yIn = (yView - HALF) / 10;
    onPlace(round2(xIn), round2(yIn));
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      onPointerDown={handlePointer}
      className="w-full max-w-md mx-auto bg-zinc-950 rounded-lg border border-zinc-800 touch-none select-none"
      style={{ aspectRatio: '1 / 1' }}
      role="application"
      aria-label="Tap-to-place shot location target"
    >
      {/* Target face */}
      <rect x={0} y={0} width={VIEW} height={VIEW} fill="#0a0a0a" />
      {/* Rings — bright outer to ember bullseye */}
      {rings.map((rIn, i) => {
        const r = rIn * 10;
        const isInner = i === rings.length - 1;
        return (
          <circle
            key={i}
            cx={HALF}
            cy={HALF}
            r={r}
            fill={isInner ? '#7c2d12' : 'none'}
            stroke={isInner ? '#fb923c' : '#52525b'}
            strokeWidth={isInner ? 1.2 : 0.8}
            opacity={isInner ? 0.9 : 0.5}
          />
        );
      })}
      {/* POA crosshairs */}
      <line x1={0} y1={HALF} x2={VIEW} y2={HALF} stroke="#3f3f46" strokeWidth={0.4} strokeDasharray="3 3" />
      <line x1={HALF} y1={0} x2={HALF} y2={VIEW} stroke="#3f3f46" strokeWidth={0.4} strokeDasharray="3 3" />

      {/* Past shots — fade older */}
      {shots.map((s, i) => {
        const cx = HALF + s.hitXIn * 10;
        const cy = HALF + s.hitYIn * 10;
        const ageOpacity = Math.max(0.3, 1 - (shots.length - i - 1) * 0.08);
        return (
          <g key={s.id} opacity={ageOpacity}>
            <circle cx={cx} cy={cy} r={1.4} fill="#fb923c" />
            <text
              x={cx + 2.5}
              y={cy + 1.5}
              fontSize="3"
              fontFamily="ui-monospace, monospace"
              fill="#fb923c"
              opacity={0.7}
            >{i + 1}</text>
          </g>
        );
      })}

      {/* Pending placement */}
      {pending && (
        <g>
          <circle
            cx={HALF + pending.x * 10}
            cy={HALF + pending.y * 10}
            r={2.2}
            fill="none"
            stroke="#22d3ee"
            strokeWidth={0.8}
          />
          <circle
            cx={HALF + pending.x * 10}
            cy={HALF + pending.y * 10}
            r={0.6}
            fill="#22d3ee"
          />
        </g>
      )}
    </svg>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
